import dynamic from 'next/dynamic';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import type { MediaWrapperTypeEnum } from '@/utils/aspectRatioUtils';

import { PromoCard } from '@/cms/Card/components/PromoType';
import type { BreadcrumbProps } from '@/components/Breadcrumb';
import { ConditionalWrapper } from '@/components/ConditionalWrapper';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useRouter } from '@/hooks/useRouter';
import type { MiscOpts } from '@/integrations/contentful';
import { EVENT_NAME } from '@/lib/gtm/const';
import { cleanObject } from '@/lib/util';

import { BaseCardType } from './components/BaseCardType';
import { CountDownType } from './components/CountDownType/CountDownType';
import { IconType } from './components/IconType';
import { ImageAndDidYouKnow } from './components/ImageAndDidYouKnow';
import { ImageTeaser } from './components/ImageTeaser';
import { IntroText } from './components/IntroText';
import { MediaAndBottomTextType } from './components/MediaAndBottomTextType';
import { MediaAndSideTextType } from './components/MediaAndSideTextType';
import { MediaAndWatchType } from './components/MediaAndWatchType';
import { MediaBannerType } from './components/MediaBannerType';
import { MyAccount } from './components/MyAccount';
import { NarrowMediaAndBottomText } from './components/NarrowMediaAndBottomText';
import { SectionBannerType } from './components/SectionBannerType';
import { SquadCardType } from './components/SquadCardType';
import { StoryType } from './components/StoryType/StoryType';
import { TextType } from './components/TextType';
import { TextWithColumnsType } from './components/TextWithColumnsType';
import { TopBanner } from './components/TopBanner';
import { transformCollectionData } from './helpers';
import type { CardProps } from './types';

import * as S from './Card.styles';

const HeroBanner = dynamic(
  () => import('./components/HeroBanner').then((mod) => mod.HeroBanner),
  { ssr: true },
);

export interface CardOpts extends BreadcrumbProps, MiscOpts {}

export const Card = (_props: CardProps) => {
  const { paths } = useRouter();
  const { formatMessage } = useIntl();
  const { sendEvent, sendPromotionEvent } = useGoogleAnalytics();
  const onClickCta = () => {
    if (props.isHeroCard) {
      sendPromotionEvent({
        event: EVENT_NAME.SELECT_PROMOTION,
        creativeName: props.internalName as string,
        creativeSlot: (props.heroCardIndex as number) + 1,
        sku: props.saleorProductSku as string,
      });
      return;
    }
    if (props.urlText === 'EXTEND MY WARRANTY') {
      sendEvent({ event: EVENT_NAME.INTENTION_WARRANTY_EXTENSION });
      return;
    }
    sendEvent({
      event: EVENT_NAME.CTA_DISCOVER,
      cta_title: props.placeholderHeading?.text?.replace(/[[\]]/g, ''),
      click_cta_name: props.urlText as string,
    });
  };

  const props: CardProps = {
    contentAlignment: 'center',
    textAlignment: 'center',
    type: 'default',
    urlType: 'button',
    visibleInDesktop: _props.visibleInDesktop,
    visibleInMobile: _props.visibleInMobile,
    onClickCta: onClickCta,
    ...cleanObject(_props),
  };
  const { type } = props;
  //TODO this only implement collection not product
  const transformedSaleorData = transformCollectionData(
    props.extraData?.collectionData || [],
    paths,
    formatMessage,
  );

  const getCard = () => {
    switch (type) {
      case 'MediaBanner':
        return (
          <MediaBannerType
            data-test-id='CardMediaBanner'
            mediaWrapperType={type}
            {...transformedSaleorData}
            {...props}
          />
        );
      case 'Text':
        return (
          <TextType
            className={type}
            data-test-id='CardText'
            {...transformedSaleorData}
            {...props}
          />
        );
      case 'IntroText':
        return (
          <IntroText
            className={type}
            data-test-id='CardIntroText'
            {...transformedSaleorData}
            {...props}
          />
        );
      case 'Icon':
        return (
          <IconType
            data-test-id='CardIcon'
            {...transformedSaleorData}
            {...props}
          />
        );
      case 'MediaAndBottomText':
        return (
          <MediaAndBottomTextType
            cardGroupType={props?.cardGroupType}
            data-test-id='CardMediaAndBottomText'
            mediaWrapperType={type}
            {...transformedSaleorData}
            {...props}
          />
        );
      case 'MediaAndSideText':
        return (
          <MediaAndSideTextType
            data-test-id='CardMediaAndSideText'
            mediaWrapperType={type}
            {...transformedSaleorData}
            {...props}
          />
        );
      case 'SectionBanner':
        return (
          <SectionBannerType
            data-test-id='CardSectionBanner'
            mediaWrapperType={type}
            {...transformedSaleorData}
            {...props}
          />
        );
      case 'TextWithColumns':
        return (
          <TextWithColumnsType
            data-test-id='CardTextWithColumns'
            {...transformedSaleorData}
            {...props}
          />
        );
      case '3by4Media':
      case 'SquadCard':
        return (
          <SquadCardType
            data-test-id='CardSquadCard'
            mediaWrapperType={type}
            {...transformedSaleorData}
            {...props}
          />
        );
      case 'TopBanner':
        return (
          <TopBanner
            data-test-id='CardTopBanner'
            mediaWrapperType={type}
            {...transformedSaleorData}
            {...props}
          />
        );
      case 'MediaAndWatch':
        return (
          <MediaAndWatchType
            data-test-id='MediaAndWatch'
            mediaWrapperType={type}
            {...transformedSaleorData}
            {...props}
          />
        );
      case 'ImageTeaser':
        return (
          <ImageTeaser
            data-test-id='CardImageTeaser'
            mediaWrapperType={type}
            {...transformedSaleorData}
            {...props}
          />
        );
      case 'ImageAndDidYouKnow':
        return (
          <ImageAndDidYouKnow
            data-test-id='MediaAndDidYouKnow'
            mediaWrapperType={type}
            {...transformedSaleorData}
            {...props}
          />
        );
      case 'promoText':
      case 'promoTitle':
        return (
          <PromoCard
            data-test-id='CardImageTeaser'
            {...transformedSaleorData}
            {...props}
          />
        );
      case 'HeroBanner':
        return (
          <HeroBanner
            data-test-id='CardDefaultType'
            mediaWrapperType={type}
            {...transformedSaleorData}
            {...props}
            preload={true}
          />
        );
      case 'NarrowMediaAndBottomText':
        return (
          <NarrowMediaAndBottomText
            data-test-id='CardNarrowMediaAndBottomText'
            mediaWrapperType={type}
            {...transformedSaleorData}
            {...props}
          />
        );
      case 'MyAccount':
        return <MyAccount {...transformedSaleorData} {...props} />;
      case 'Story':
        return (
          <StoryType
            mediaWrapperType={type}
            {...transformedSaleorData}
            {...props}
          />
        );
      case 'GenericCountdownCard':
        return (
          <CountDownType
            data-test-id='CardCountDown'
            mediaWrapperType={type}
            {...transformedSaleorData}
            {...props}
          />
        );
      default:
        return (
          <BaseCardType
            data-test-id='DefaultBaseCardType'
            mediaWrapperType={type as MediaWrapperTypeEnum}
            {...props}
          />
        );
    }
  };

  return (
    <ConditionalWrapper
      condition={(props.id ?? false) as boolean}
      wrapper={(CardComponent: ReactNode) => (
        <S.CardWrapper id={props.id ?? ''}>{CardComponent}</S.CardWrapper>
      )}
    >
      {getCard()}
    </ConditionalWrapper>
  );
};

Card.displayName = 'Card';
